import { useQueries } from "@tanstack/react-query";
import superbillByAppointmentIdsQueryOptions from "queries/superbillByAppointmentIdsQueryOptions";
import { useMemo } from "react";

const PAGE_SIZE = 100;

const useFetchSuperbillsForAppointments = <T extends AppointmentMetaData>(
  appointments: T[],
  onBehalfOf?: string,
) => {
  const appointmentIdPages = useMemo(() => {
    const pages: string[][] = [];
    for (let i = 0; i < appointments.length; i += PAGE_SIZE) {
      pages.push(
        appointments
          .slice(i, i + PAGE_SIZE)
          .map((el) => el.appointment_id.toString()),
      );
    }
    return pages.filter((page) => page.length > 0);
  }, [appointments]);

  const superbillsQueries = useQueries({
    queries: appointmentIdPages.map((page) =>
      superbillByAppointmentIdsQueryOptions({
        appointmentIds: page,
        onBehalfOf,
      }),
    ),
  });

  const superbills = useMemo(() => {
    return superbillsQueries
      .flatMap((query) => query.data ?? [])
      .reduce((acc, superbill) => {
        // If no existing superbill, add the new one
        const existingSuperbill = acc.find(
          (s) => s.statement_no === superbill.statement_no,
        );

        if (!existingSuperbill) {
          return [...acc, superbill];
        }

        if (existingSuperbill.modify_datetime >= superbill.modify_datetime) {
          // If the existing superbill is more recent, keep it
          return acc;
        }

        // If the existing superbill is older, replace it
        return acc.map((s) =>
          s.statement_no === superbill.statement_no ? superbill : s,
        );
      }, [] as SuperBillMetaData[]);
  }, [superbillsQueries]);

  const appointmentsWithSuperBill = useMemo(() => {
    const superbillsByAppointmentId = superbills.reduce((acc, superbill) => {
      superbill.appointment_ids.forEach((appointmentId) => {
        if (!acc[appointmentId]) {
          acc[appointmentId] = [];
        }
        acc[appointmentId].push(superbill);
      });
      return acc;
    }, {} as Record<number, SuperBillMetaData[]>);

    return appointments.map((appointment) => ({
      ...appointment,
      superbills:
        superbillsByAppointmentId[appointment.appointment_id]?.sort((a, b) => {
          if (a.statement_no < b.statement_no) return 1;
          if (a.statement_no > b.statement_no) return -1;
          return 0;
        }) || [],
    }));
  }, [appointments, superbills]);

  return {
    isLoadingSuperbills: superbillsQueries.some(
      (query) => query.isLoading || query.isPending,
    ),
    appointmentsWithSuperBill,
  };
};

export default useFetchSuperbillsForAppointments;
